import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Container,
  FormTitle,
  Title,
  Error,
  Form,
  SignUp,
  ForgotPassword,
  StyledButtonPrimary,
  DivBadgeStyled,
  Divider,
  AccessCodeButton,
  AccessCodeHelp,
  AccessCodeMessage,
  TextButton,
  AccessCodeLinks,
} from "./styles";
import { Creators as AuthActions } from "../../store/ducks/auth";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import HelmetCustom from "../../components/HelmetCustom";
import InputCustom from "../../components/InputCustom";
import BadgePaymentProvider from "../../components/BadgePaymentProvider";
import { ToastContainer } from "react-toastify";
import MainLayoutCompact from "../../components/MainLayoutCompact";

const SignInSchema = Yup.object().shape({
  email: Yup.string()
    .email("Informe um e-mail válido")
    .required("E-mail não pode ser vazio"),
  password: Yup.string().required("Senha não pode ser vazia")
});

const IdentifierSchema = Yup.object().shape({
  identifier: Yup.string()
    .test("cpf-or-email", "Informe um CPF ou e-mail válido", value => {
      const normalized = String(value || "").trim();
      return Yup.string().email().isValidSync(normalized) || normalized.replace(/\D/g, "").length === 11;
    })
    .required("CPF ou e-mail não pode ser vazio")
});

const CodeSchema = Yup.object().shape({
  code: Yup.string().matches(/^\d{6}$/, "Informe os 6 dígitos").required("Código não pode ser vazio")
});

export default function SignIn({ history }) {
  const dispatch = useDispatch();
  const { loading: loadingSignIn, error, accessCodeSent, accessCodeMessage } = useSelector(state => state.auth);
  const { data } = useSelector(state => state.store);
  const [mode, setMode] = useState("password");
  const [identifier, setIdentifier] = useState("");
  const [secondsToResend, setSecondsToResend] = useState(60);
  const accessCodeEnabled = data?.empresa?.login_codigo_acesso_ativo === true;

  useEffect(() => {
    dispatch(AuthActions.resetError());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      dispatch(AuthActions.resetError());
      dispatch(AuthActions.resetAccessCode());
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accessCodeSent) {
      setMode("verify");
      setSecondsToResend(60);
    }
  }, [accessCodeSent]);

  useEffect(() => {
    if (mode !== "verify" || secondsToResend === 0) return undefined;
    const timer = setTimeout(() => setSecondsToResend(value => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [mode, secondsToResend]);

  const redirectCheckout = () => {
    const previousPage = history.location.state?.previousPage ?? "";
    return previousPage === "/carrinho" || previousPage.includes("/carrinho/");
  };

  function handleSubmit(values, { setSubmitting }) {
    const { email, password } = values;

    // `state` pode existir sem `previousPage` (back/forward do navegador) — o
    // acesso direto estourava TypeError no submit.
    dispatch(AuthActions.signInRequest(email, password, redirectCheckout()));
    setTimeout(() => {
      setSubmitting(false);
    }, 400);
  }

  function openAccessCode(email) {
    dispatch(AuthActions.resetAccessCode());
    setIdentifier(email || "");
    setMode("request");
  }

  function requestCode(values, { setSubmitting }) {
    const normalizedIdentifier = values.identifier.trim();
    setIdentifier(normalizedIdentifier);
    dispatch(AuthActions.requestAccessCode(normalizedIdentifier));
    setSubmitting(false);
  }

  function verifyCode(values, { setSubmitting }) {
    dispatch(AuthActions.verifyAccessCode(identifier, values.code, redirectCheckout()));
    setSubmitting(false);
  }

  function backToPassword() {
    dispatch(AuthActions.resetAccessCode());
    setMode("password");
  }

  return (
    <MainLayoutCompact history={history}>
      <HelmetCustom pageTitle="Entrar" />
      <Container>
        <ToastContainer autoClose={2500} />

        {mode === "password" && <Formik
          initialValues={{ email: "", password: "" }}
          validate={values => { }}
          onSubmit={handleSubmit}
          validationSchema={SignInSchema}
        >
          {({
            values,
            handleChange,
            handleBlur,
            handleSubmit,
            errors,
            isValid,
            touched,
            isSubmitting
          }) => (
            <>
              <Form onSubmit={handleSubmit}>
                <FormTitle>
                  <Title>Já tenho uma conta</Title>
                </FormTitle>
                {error && <Error>{error}</Error>}
                <InputCustom id="signInEmail" name="email" type="text" description="E-mail" value={values.email} onChange={handleChange} onBlur={handleBlur} isInvalid={errors.email && touched.email} errors={errors.email} />
                <InputCustom id="password" name="password" type="password" description="Senha" value={values.password} onChange={handleChange} onBlur={handleBlur} isInvalid={errors.password && touched.password} errors={errors.password} />
                <ForgotPassword to="/esqueci-senha">
                  Esqueci minha senha
                </ForgotPassword>
                <StyledButtonPrimary text={loadingSignIn ? "Entrando..." : "Entrar"} disabled={isSubmitting || !isValid || loadingSignIn} />
                {accessCodeEnabled && (
                  <>
                    <Divider><span>ou</span></Divider>
                    <AccessCodeButton
                      type="button"
                      text="Receber código de acesso"
                      onClick={() => openAccessCode(values.email)}
                    />
                    <AccessCodeHelp>
                      Entre na sua conta com o código que enviaremos para seu e-mail.
                    </AccessCodeHelp>
                  </>
                )}
                <SignUp>
                  Ainda não tem cadastro?
                  <Link to="/criar-conta">Criar minha conta</Link>
                </SignUp>
                <DivBadgeStyled>
                  <BadgePaymentProvider
                    provedor_pagamento_badge={
                      data?.empresa?.provedor_pagamento_badge
                    }
                    provedor_pgto_badge_ref={
                      data?.empresa?.provedor_pgto_badge_ref
                    }
                  />
                </DivBadgeStyled>
              </Form>
            </>
          )}
        </Formik>}

        {mode === "request" && (
          <Formik
            initialValues={{ identifier }}
            isInitialValid={IdentifierSchema.isValidSync({ identifier })}
            validationSchema={IdentifierSchema}
            onSubmit={requestCode}
          >
            {({ values, handleChange, handleBlur, handleSubmit, errors, touched, isValid, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                <FormTitle><Title>Receber código de acesso</Title></FormTitle>
                {error && <Error>{error}</Error>}
                <InputCustom
                  id="accessCodeIdentifier"
                  name="identifier"
                  type="text"
                  description="CPF ou e-mail"
                  value={values.identifier}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={errors.identifier && touched.identifier}
                  errors={errors.identifier}
                  autoComplete="username"
                />
                <StyledButtonPrimary text={loadingSignIn ? "Enviando..." : "Enviar código"} disabled={isSubmitting || !isValid || loadingSignIn} />
                <TextButton type="button" onClick={backToPassword}>Entrar com senha</TextButton>
              </Form>
            )}
          </Formik>
        )}

        {mode === "verify" && (
          <Formik initialValues={{ code: "" }} validationSchema={CodeSchema} onSubmit={verifyCode}>
            {({ values, handleBlur, handleSubmit, errors, touched, isValid, isSubmitting, setFieldValue }) => (
              <Form onSubmit={handleSubmit}>
                <FormTitle><Title>Digite o código</Title></FormTitle>
                <AccessCodeMessage>{accessCodeMessage}</AccessCodeMessage>
                {error && <Error>{error}</Error>}
                <InputCustom
                  id="accessCode"
                  name="code"
                  type="text"
                  description="Código de 6 dígitos"
                  value={values.code}
                  onChange={event => {
                    const code = event.target.value.replace(/\D/g, "").slice(0, 6);
                    setFieldValue("code", code);
                    if (code.length === 6) setTimeout(handleSubmit, 0);
                  }}
                  onBlur={handleBlur}
                  isInvalid={errors.code && touched.code}
                  errors={errors.code}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                />
                <StyledButtonPrimary text={loadingSignIn ? "Validando..." : "Entrar"} disabled={isSubmitting || !isValid || loadingSignIn} />
                <TextButton
                  type="button"
                  disabled={secondsToResend > 0 || loadingSignIn}
                  onClick={() => {
                    dispatch(AuthActions.requestAccessCode(identifier));
                    setSecondsToResend(60);
                  }}
                >
                  {secondsToResend > 0 ? `Reenviar código em ${secondsToResend}s` : "Reenviar código"}
                </TextButton>
                <AccessCodeLinks>
                  <TextButton type="button" onClick={() => { dispatch(AuthActions.resetAccessCode()); setMode("request"); }}>Voltar</TextButton>
                  <TextButton type="button" onClick={backToPassword}>Entrar com senha</TextButton>
                </AccessCodeLinks>
              </Form>
            )}
          </Formik>
        )}
      </Container>
    </MainLayoutCompact>
  );
}
